/**
 * Ce que le paquet d'entree de l'app sait de la diffusion, sans charger son ecran : l'adresse, les sept onglets, le lien
 * de la regie, la phrase d'attente et celle d'un chargement qui a echoue, et les deux phrases que la carte « Agent du
 * PC » de la regie partage avec les onglets (« Réessayer », le compte sans acces).
 *
 * L'ecran lui-meme (`diffusion.tsx`, ses onglets et `diffusion-texts.ts`, a lui seul pres de 50 ko) est charge a la
 * demande par `routes.tsx` : ce qui est lu avant lui vit ici, et rien de ce fichier n'importe `diffusion-texts.ts`
 * (`routes.test.tsx` suit les imports du paquet d'entree et le verifie). `diffusion-texts.ts` re-exporte ces noms, pour
 * que les ecrans n'aient qu'une source.
 */

/** L'adresse de l'ecran. La route la declare, le backend y renvoie apres l'autorisation de YouTube. */
export const DIFFUSION_PATH = "/regie/diffusion";

/**
 * Les sept valeurs du parametre `onglet`, dans l'ordre de la barre : chacune a son ecran. Le dernier mot de
 * l'adresse est celui de la route (`routes.tsx`), qui ne laisse passer que ces sept valeurs.
 */
export const ONGLETS = [
  "semaine",
  "programme",
  "stock",
  "suivi",
  "chiffres",
  "reseaux",
  "reglages",
] as const;
export type Onglet = (typeof ONGLETS)[number];

/** L'onglet que montre une adresse sans `onglet`, ou avec une valeur inconnue. */
export const ONGLET_PAR_DEFAUT: Onglet = "suivi";

/**
 * Vrai pour l'une des cinq valeurs, et pour rien d'autre. Un test d'appartenance a la liste fermee, pas
 * `value in ...` : `constructor` ou `__proto__` y passeraient. La valeur vient de l'adresse, donc de n'importe
 * qui, et le routeur lit les valeurs comme du JSON.
 */
export function isOnglet(value: unknown): value is Onglet {
  return typeof value === "string" && (ONGLETS as readonly string[]).includes(value);
}

/**
 * Les cinq reseaux dont l'autorisation renvoie ici, avec le code du retour dans `?<reseau>=<code>`. La liste est
 * celle de `RESEAUX_AUTORISABLES` du contrat (`diffusion-texts.test.ts` le verifie) : elle est reecrite ici parce que
 * ce fichier n'importe rien, le paquet d'entree restant petit.
 */
export const RESEAUX_DE_RETOUR = ["youtube", "instagram", "facebook", "threads", "tiktok"] as const;
export type ReseauDeRetour = (typeof RESEAUX_DE_RETOUR)[number];

/** Les codes `?<reseau>=<code>` de l'adresse, tels que la route les a lus : du texte, pas encore juge. */
export type RetoursAutorisation = Partial<Record<ReseauDeRetour, string | undefined>>;

/** Le parametre d'adresse ramene a du texte : le routeur le lit comme du JSON (`?youtube=123` donne un nombre). */
export const texteOuRien = (valeur: unknown): string | undefined =>
  valeur === undefined ? undefined : String(valeur);

/** Le lien de la regie vers cet ecran, et le nom de la navigation qui le porte. */
export const DIFFUSION_LINK = "Diffusion";
export const REGIE_NAV_LABEL = "Pages de la régie";

/** Le temps que l'ecran de la diffusion se charge (il est livre a part du reste de l'app). */
export const DIFFUSION_CHARGEMENT = "Chargement de la diffusion...";

/**
 * Quand son chargement echoue : le plus souvent l'app a ete mise a jour depuis l'ouverture de l'onglet, et le fichier
 * de l'ancienne version n'existe plus. Recharger la page va chercher la nouvelle.
 */
export const DIFFUSION_INDISPONIBLE =
  "L'écran de la diffusion n'a pas pu être chargé. Recharge la page : Castellan a peut-être été mis à jour depuis l'ouverture de cet onglet.";
export const DIFFUSION_RECHARGER = "Recharger la page";

/** Le ton d'une phrase : le rouge pour ce qui attend un geste de Nicolas, le gris pour ce qui est attendu. */
export type Ton = "ok" | "neutre" | "problem";

export const RETRY = "Réessayer";

/** Ce que dit un onglet quand la regie rend 403 : le compte connecte n'est pas celui de l'admin. */
export const SESSION_FORBIDDEN = "Ce compte n'a pas accès à la régie.";
